import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Search, Building2, AlertCircle, Edit2, Trash2 } from 'lucide-react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { CondominioCard } from '@/components/Cards';
import { CondominioForm } from '@/components/Forms';
import { useCondomini } from '@/hooks/useCondomini';
import type { Condominio } from '@/lib/index';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

export default function Condomini() {
  const { condomini, loading, deleteCondominio, createCondominio, updateCondominio } = useCondomini();
  const [searchQuery, setSearchQuery] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingCondominio, setEditingCondominio] = useState<Condominio | undefined>();
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [condominioToDelete, setCondominioToDelete] = useState<string | null>(null);

  const filteredCondomini = condomini.filter((c) =>
    c.nome.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.indirizzo && c.indirizzo.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleEdit = (condominio: Condominio) => {
    setEditingCondominio(condominio);
    setFormOpen(true);
  };

  const handleDelete = (id: string) => {
    setCondominioToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!condominioToDelete) return;

    try {
      await deleteCondominio(condominioToDelete);
      toast.success('Condominio eliminato con successo');
      setDeleteDialogOpen(false);
      setCondominioToDelete(null);
    } catch {
      toast.error('Errore durante l\'eliminazione del condominio');
    }
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditingCondominio(undefined);
    setFormError(null);
  };

  const handleFormSuccess = async (data: any) => {
    setFormError(null);
    try {
      if (editingCondominio) {
        await updateCondominio(editingCondominio.id, data);
        toast.success('Condominio aggiornato con successo');
        handleFormClose();
      } else {
        await createCondominio(data);
        toast.success('Condominio creato con successo');
        handleFormClose();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Errore durante il salvataggio';
      // Mostra errore sia come toast sia inline nel dialog
      setFormError(msg);
      toast.error(msg, { duration: 6000 });
    }
  };

  return (
    <DashboardLayout title="Condomini">
      <div className="space-y-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Condomini</h1>
            <p className="text-muted-foreground mt-1">Gestisci i tuoi condomini</p>
          </div>
          <Button
            onClick={() => setFormOpen(true)}
            className="gap-2"
            size="lg"
          >
            <Plus className="h-5 w-5" />
            Nuovo Condominio
          </Button>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Cerca per nome o indirizzo..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12"
          />
        </div>

        {loading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="space-y-4 rounded-xl border bg-card p-6">
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3" />
                <div className="flex gap-2 pt-4">
                  <Skeleton className="h-9 flex-1" />
                  <Skeleton className="h-9 flex-1" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredCondomini.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="flex flex-col items-center justify-center py-16 text-center"
          >
            <div className="rounded-full bg-muted p-6 mb-6">
              {searchQuery ? (
                <AlertCircle className="h-12 w-12 text-muted-foreground" />
              ) : (
                <Building2 className="h-12 w-12 text-muted-foreground" />
              )}
            </div>
            <h3 className="text-xl font-semibold mb-2">
              {searchQuery ? 'Nessun risultato trovato' : 'Nessun condominio'}
            </h3>
            <p className="text-muted-foreground mb-6 max-w-md">
              {searchQuery
                ? 'Prova a modificare i criteri di ricerca'
                : 'Inizia aggiungendo il tuo primo condominio per gestire documenti e analisi'}
            </p>
            {!searchQuery && (
              <Button onClick={() => setFormOpen(true)} size="lg" className="gap-2">
                <Plus className="h-5 w-5" />
                Aggiungi Condominio
              </Button>
            )}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
          >
            <AnimatePresence mode="popLayout">
              {filteredCondomini.map((condominio, index) => (
                <motion.div
                  key={condominio.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <CondominioCard
                    condominio={condominio}
                    onEdit={() => handleEdit(condominio)}
                    onDelete={() => handleDelete(condominio.id)}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </div>

      <Dialog open={formOpen} onOpenChange={handleFormClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCondominio ? 'Modifica Condominio' : 'Nuovo Condominio'}
            </DialogTitle>
          </DialogHeader>
          {formError && (
            <div className="flex items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3">
              <AlertCircle className="h-5 w-5 text-destructive mt-0.5 shrink-0" />
              <p className="text-sm text-destructive font-medium">{formError}</p>
            </div>
          )}
          <CondominioForm
            onSubmit={handleFormSuccess}
            initialData={editingCondominio}
            onCancel={handleFormClose}
          />
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare questo condominio? Questa azione eliminerà anche tutti i documenti e le analisi associate. L'operazione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}